import brands from '@/services/dictionaries/brands.json'
import categories from '@/services/dictionaries/categories.json'
import memories from '@/services/dictionaries/memories.json'
import sockets from '@/services/dictionaries/sockets.json'
import storage from '@/services/dictionaries/storage.json'
import technologies from '@/services/dictionaries/technologies.json'
import { productNormalizer } from './ProductNormalizer'
import type { ParsedProductHints } from './ProductTypes'

const BRAND_ALIASES: Record<string, string> = {
  WD: 'Western Digital',
  WDC: 'Western Digital',
  ASUS: 'Asus',
  HPE: 'HP',
  NVIDIA: 'NVIDIA',
  MIKROTIK: 'Mikrotik',
  INTELBRAS: 'Intelbras',
}

const CATEGORY_HINTS: Array<{ category: string; patterns: RegExp[] }> = [
  { category: 'SSD', patterns: [/\bSSD\b/i, /\bNVME\b/i, /\bM\.?2\b/i, /\bSN\d{3,4}\b/i] },
  { category: 'RAM', patterns: [/\bRAM\b/i, /\bMEMORIA\b/i, /\bDDR[345]\b/i, /\bFURY\b/i, /\bVENGEANCE\b/i] },
  { category: 'CPU', patterns: [/\bRYZEN\b/i, /\bCORE\s*I[3579]\b/i, /\bINTEL\s*I[3579]\b/i, /\bPROCESSADOR\b/i] },
  { category: 'GPU', patterns: [/\bRTX\b/i, /\bGTX\b/i, /\bRADEON\b/i, /\bGEFORCE\b/i] },
  { category: 'Notebook', patterns: [/\bNOTEBOOK\b/i, /\bLAPTOP\b/i, /\bLATITUDE\b/i, /\bPROBOOK\b/i, /\bTHINKPAD\b/i] },
  { category: 'Servidor', patterns: [/\bSERVIDOR\b/i, /\bPOWEREDGE\b/i, /\bDL\d{3}\b/i, /\bPROLIANT\b/i] },
  { category: 'Placa Mãe', patterns: [/\bPLACA[-\s]?MAE\b/i, /\bMOTHERBOARD\b/i, /\bMOBO\b/i] },
  { category: 'Monitor', patterns: [/\bMONITOR\b/i] },
  { category: 'Fonte', patterns: [/\bFONTE\b/i, /\bPSU\b/i] },
  { category: 'Gabinete', patterns: [/\bGABINETE\b/i, /\bCASE\b/i] },
  { category: 'Switch', patterns: [/\bSWITCH\b/i] },
  { category: 'Firewall', patterns: [/\bFIREWALL\b/i] },
  { category: 'Access Point', patterns: [/\bACCESS\s*POINT\b/i, /\bAP\b/i, /\bUNIFI\b/i] },
  { category: 'Storage', patterns: [/\bSTORAGE\b/i, /\bNAS\b/i] },
  { category: 'No-break', patterns: [/\bNO[-\s]?BREAK\b/i, /\bUPS\b/i] },
  { category: 'Desktop', patterns: [/\bDESKTOP\b/i, /\bPC\b/i] },
]

const LINE_HINTS: Array<{ brand?: string; line: string; pattern: RegExp }> = [
  { brand: 'Dell', line: 'Latitude', pattern: /\bLATITUDE\b/i },
  { brand: 'Dell', line: 'PowerEdge', pattern: /\bPOWEREDGE\b/i },
  { brand: 'Dell', line: 'OptiPlex', pattern: /\bOPTIPLEX\b/i },
  { brand: 'HP', line: 'ProBook', pattern: /\bPROBOOK\b/i },
  { brand: 'HP', line: 'EliteBook', pattern: /\bELITEBOOK\b/i },
  { brand: 'HP', line: 'ProLiant', pattern: /\bPROLIANT\b/i },
  { brand: 'Lenovo', line: 'ThinkPad', pattern: /\bTHINKPAD\b/i },
  { brand: 'Lenovo', line: 'IdeaPad', pattern: /\bIDEAPAD\b/i },
  { brand: 'Kingston', line: 'Fury', pattern: /\bFURY\b/i },
  { brand: 'Kingston', line: 'NV', pattern: /\bNV\d\b/i },
  { brand: 'Corsair', line: 'Vengeance', pattern: /\bVENGEANCE\b/i },
  { brand: 'Samsung', line: '990', pattern: /\b990\b/i },
  { brand: 'Western Digital', line: 'SN', pattern: /\bSN\d{3,4}\b/i },
  { brand: 'AMD', line: 'Ryzen', pattern: /\bRYZEN\b/i },
  { brand: 'Intel', line: 'Core', pattern: /\bCORE\s*I[3579]\b/i },
  { brand: 'NVIDIA', line: 'GeForce', pattern: /\bRTX\b|\bGTX\b/i },
]

const PART_NUMBER_PATTERNS: RegExp[] = [
  /\bSNV3S\d{3,5}G?\b/i,
  /\bSNV3S\b/i,
  /\bMZ-[A-Z0-9]+\b/i,
  /\bWDS\d+[A-Z0-9]+\b/i,
  /\bCT\d+[A-Z0-9]+\b/i,
  /\bKF\d+[A-Z0-9]+\b/i,
  /\bCMK\d+[A-Z0-9]+\b/i,
  /\bBX\d+[A-Z0-9]+\b/i,
]

/** Known marketplace PN inference when ads omit official part numbers. */
const KNOWN_PART_NUMBERS: Array<{
  brand: RegExp
  model: RegExp
  capacity?: RegExp
  partNumber: string
}> = [
  { brand: /kingston/i, model: /\bNV3\b/i, capacity: /1\s*TB/i, partNumber: 'SNV3S1000G' },
  { brand: /kingston/i, model: /\bNV3\b/i, capacity: /500\s*GB/i, partNumber: 'SNV3S500G' },
  { brand: /kingston/i, model: /\bNV3\b/i, capacity: /2\s*TB/i, partNumber: 'SNV3S2000G' },
  { brand: /samsung/i, model: /990\s*EVO/i, capacity: /1\s*TB/i, partNumber: 'MZ-V9E1T0B' },
  { brand: /western|wd/i, model: /\bSN770\b/i, capacity: /1\s*TB/i, partNumber: 'WDS100T3X0E' },
]

/**
 * ProductParser — turns free-text IT descriptions into structured hints.
 */
export class ProductParser {
  private brandList: string[]
  private techList: string[]
  private socketList: string[]

  constructor() {
    this.brandList = [...brands].sort((a, b) => b.length - a.length)
    this.techList = uniqueCi([...technologies, ...memories, ...storage]).sort(
      (a, b) => b.length - a.length,
    )
    this.socketList = [...sockets]
  }

  parse(raw: string): ParsedProductHints {
    const normalized = productNormalizer.normalize(raw)
    const tokens = productNormalizer.tokenize(raw)

    const brand = this.detectBrand(raw, tokens)
    const category = this.detectCategory(raw, tokens)
    const line = this.detectLine(raw, brand)
    const capacity = this.detectCapacity(raw)
    const technology = this.detectTechnologies(raw)
    const socket = this.detectSocket(raw)
    const generation = this.detectGeneration(raw)
    const attributes = this.detectAttributes(raw, category)
    let model = this.detectModel(raw, tokens, brand, line, category, '')
    const partNumber =
      this.detectPartNumber(raw) || this.inferKnownPartNumber(raw, brand, model, capacity)
    if (partNumber) {
      const refined = this.detectModel(raw, tokens, brand, line, category, partNumber)
      if (refined) model = refined
    }
    model = this.canonicalizeModel(model, partNumber, brand)

    return {
      raw,
      normalized,
      category,
      brand,
      line,
      model,
      partNumber,
      capacity,
      technology,
      socket,
      generation,
      attributes,
      tokens,
    }
  }

  private detectBrand(raw: string, tokens: string[]): string {
    // OEM / product line brands win over CPU vendor mentions (Intel i5 inside a Dell notebook)
    if (/\b(LATITUDE|POWEREDGE|OPTIPLEX|PRECISION|XPS)\b/i.test(raw)) return 'Dell'
    if (/\b(PROBOOK|ELITEBOOK|PROLIANT|ELITEDESK|DL\d{3})\b/i.test(raw)) return 'HP'
    if (/\b(THINKPAD|IDEAPAD|THINKCENTRE|LEGION)\b/i.test(raw)) return 'Lenovo'
    if (/\b(KINGSTON|FURY|NV\d|SNV3)\b/i.test(raw) && !/\b(DELL|HP|LENOVO|ASUS)\b/i.test(raw)) {
      if (/\bKINGSTON\b/i.test(raw)) return 'Kingston'
    }

    const upper = productNormalizer.normalize(raw)
    for (const brand of this.brandList) {
      const n = productNormalizer.normalize(brand)
      // Avoid classifying CPU vendor as product brand when an OEM brand is present later
      if ((n === 'INTEL' || n === 'AMD') && /\b(DELL|HP|LENOVO|ASUS|LATITUDE|PROBOOK)\b/i.test(raw)) {
        continue
      }
      if (upper.includes(n) || tokens.includes(n)) {
        return BRAND_ALIASES[n] ?? brand
      }
    }
    if (/\bRYZEN\b/i.test(raw)) return 'AMD'
    if (/\b(CORE\s*)?I[3579]\b/i.test(raw) || /\bINTEL\b/i.test(raw)) return 'Intel'
    if (/\bRTX\b|\bGTX\b/i.test(raw)) return 'NVIDIA'
    if (/\bDL\d{3}\b/i.test(raw)) return 'HP'
    if (/\bPOWEREDGE\b/i.test(raw)) return 'Dell'
    return ''
  }

  private detectCategory(raw: string, _tokens: string[]): string {
    // Prefer host products over component keywords embedded in the same sentence
    if (/\b(NOTEBOOK|LAPTOP|LATITUDE|PROBOOK|THINKPAD|ELITEBOOK|IDEAPAD)\b/i.test(raw)) {
      return 'Notebook'
    }
    if (/\b(SERVIDOR|POWEREDGE|PROLIANT|DL\d{3})\b/i.test(raw)) return 'Servidor'
    if (/\b(DESKTOP|OPTIPLEX)\b/i.test(raw)) return 'Desktop'

    for (const entry of CATEGORY_HINTS) {
      if (entry.patterns.some((p) => p.test(raw))) return entry.category
    }
    for (const category of categories) {
      if (new RegExp(`\\b${escapeReg(category)}\\b`, 'i').test(raw)) return category
    }
    return 'Geral'
  }

  private detectLine(raw: string, brand: string): string {
    for (const hint of LINE_HINTS) {
      if (hint.brand && brand && !equalsCi(hint.brand, brand)) continue
      if (hint.pattern.test(raw)) return hint.line
    }
    return ''
  }

  private canonicalizeModel(model: string, partNumber: string, brand: string): string {
    const m = productNormalizer.normalize(model)
    const pn = productNormalizer.normalize(partNumber)
    if (/^SNV3/i.test(m) || /^SNV3/i.test(pn)) return 'NV3'
    if (/^SN770/i.test(m) || /SN770/i.test(pn)) return 'SN770'
    if (/kingston/i.test(brand) && /^NV\d$/i.test(m)) return m
    return model
  }

  private detectPartNumber(raw: string): string {
    for (const pattern of PART_NUMBER_PATTERNS) {
      const match = raw.match(pattern)
      if (match?.[0]) return productNormalizer.normalize(match[0]).replace(/\s+/g, '')
    }
    const snv = raw.match(/\b(SNV3S\w+)\b/i)
    if (snv?.[1]) return productNormalizer.normalize(snv[1])
    return ''
  }

  private inferKnownPartNumber(
    raw: string,
    brand: string,
    model: string,
    capacity: string,
  ): string {
    const hay = `${brand} ${model} ${capacity} ${raw}`
    for (const known of KNOWN_PART_NUMBERS) {
      if (!known.brand.test(hay)) continue
      if (!known.model.test(hay)) continue
      if (known.capacity && !known.capacity.test(hay)) continue
      return known.partNumber
    }
    return ''
  }

  private detectCapacity(raw: string): string {
    const match =
      raw.match(/\b(\d+(?:[.,]\d+)?)\s*(TB|GB|MB|T|G)\b/i) ??
      raw.match(/\b(\d+)\s*(TB|GB)\b/i)
    if (!match) return ''
    const value = match[1]!.replace(',', '.')
    let unit = match[2]!.toUpperCase()
    if (unit === 'T') unit = 'TB'
    if (unit === 'G') unit = 'GB'
    return `${value}${unit}`
  }

  private detectTechnologies(raw: string): string[] {
    const found: string[] = []
    for (const tech of this.techList) {
      const pattern = new RegExp(`\\b${escapeReg(tech).replace('\\ ', '\\s*')}\\b`, 'i')
      if (pattern.test(raw)) found.push(tech)
    }
    // PCIe generation compound
    const pcie = raw.match(/\bPCIe?\s*([345])(?:\.0)?\b/i)
    if (pcie?.[1]) {
      const label = `PCIe ${pcie[1]}.0`
      if (!found.some((f) => equalsCi(f, label))) found.push(label)
    }
    return uniqueCi(found)
  }

  private detectSocket(raw: string): string | undefined {
    for (const socket of this.socketList) {
      if (new RegExp(`\\b${escapeReg(socket)}\\b`, 'i').test(raw)) return socket
    }
    return undefined
  }

  private detectGeneration(raw: string): string | undefined {
    const gen = raw.match(/\b(GEN\s*[345]|[345](RD|TH)\s*GEN)\b/i)
    if (gen?.[1]) return productNormalizer.normalize(gen[1])
    const series = raw.match(/\b(12|13|14|15)(TH)?\s*GEN\b/i)
    if (series?.[1]) return `${series[1]}TH GEN`
    return undefined
  }

  private detectAttributes(raw: string, category: string): Record<string, string> {
    const attributes: Record<string, string> = {}

    const cpu =
      raw.match(/\b(Intel\s*i[3579](?:-\d{4,5}[A-Z]*)?)\b/i) ??
      raw.match(/\b(Core\s*i[3579](?:-\d{4,5}[A-Z]*)?)\b/i) ??
      raw.match(/\b(Ryzen\s*[3579]\s*\d{4}[A-Z]*)\b/i) ??
      raw.match(/\b(i[3579](?:-\d{4,5}[A-Z]*)?)\b/i)
    if (cpu?.[1]) attributes.cpu = normalizeAttr(cpu[1])

    const ram = raw.match(/\b(\d+\s*GB)\s*(RAM|DDR[345])?\b/i)
    if (ram?.[1] && (category === 'Notebook' || /RAM|DDR/i.test(raw))) {
      attributes.ram = productNormalizer.normalize(ram[1]).replace(/\s+/g, '')
    }

    const ssd =
      raw.match(/\bSSD\s*(\d+\s*GB|\d+\s*TB|\d{2,4})\b/i) ??
      raw.match(/\b(\d+\s*GB|\d+\s*TB)\s*SSD\b/i)
    if (ssd?.[1]) {
      const value = productNormalizer.normalize(ssd[1]).replace(/\s+/g, '')
      attributes.ssd = /^\d+$/.test(value) ? `${value}GB` : value
    }

    const rtx = raw.match(/\b(RTX\s*\d{4}(?:\s*TI)?)\b/i)
    if (rtx?.[1]) attributes.gpu = normalizeAttr(rtx[1])

    return attributes
  }

  private detectModel(
    raw: string,
    tokens: string[],
    brand: string,
    line: string,
    category: string,
    partNumber: string,
  ): string {
    // Explicit model patterns by category/brand
    const latitude = raw.match(/\bLatitude\s+(\d{3,5}[A-Z0-9]*)\b/i)
    if (latitude?.[1]) return latitude[1]!.toUpperCase()

    const probook = raw.match(/\bProBook\s+(\d{3,5}[A-Z0-9]*)\b/i)
    if (probook?.[1]) return probook[1]!.toUpperCase()

    const poweredge = raw.match(/\bPowerEdge\s+([A-Z]?\d{3,4}[A-Z0-9]*)\b/i)
    if (poweredge?.[1]) return poweredge[1]!.toUpperCase()

    const dl = raw.match(/\b(DL\d{3}[A-Z0-9]*)\b/i)
    if (dl?.[1]) return dl[1]!.toUpperCase()

    const nv = raw.match(/\bNV(\d)\b/i)
    if (nv && /kingston|ssd/i.test(raw)) return `NV${nv[1]}`

    const sn = raw.match(/\b(SN\d{3,4}[A-Z]*)\b/i)
    if (sn?.[1] && /wd|western|ssd/i.test(raw)) return sn[1]!.toUpperCase()

    const evo = raw.match(/\b(990\s*EVO(?:\s*PLUS)?)\b/i)
    if (evo?.[1]) return productNormalizer.normalize(evo[1]).replace(/\s+/g, ' ')

    const ryzen = raw.match(/\bRyzen\s*([3579]\s*\d{4}X?)\b/i)
    if (ryzen?.[1]) return productNormalizer.normalize(ryzen[1]).replace(/\s+/g, '')

    const intelCore = raw.match(/\b(?:Intel\s*)?(?:Core\s*)?(i[3579](?:-\d{4,5}[A-Z]*)?)\b/i)
    if (intelCore?.[1] && (category === 'CPU' || /intel|core|processador/i.test(raw))) {
      return productNormalizer.normalize(intelCore[1])
    }

    const rtx = raw.match(/\b(RTX\s*\d{4}(?:\s*TI)?)\b/i)
    if (rtx?.[1]) return productNormalizer.normalize(rtx[1]).replace(/\s+/g, ' ')

    const intelbras = raw.match(/\b(?:AP|Roteador|Switch)?\s*(\d{3,4}(?:\s*[A-Z]+)*)\b/i)
    if (intelbras?.[1] && /intelbras/i.test(raw)) {
      return productNormalizer.normalize(intelbras[1]).replace(/\s+/g, ' ')
    }

    const mikrotik = raw.match(/\b(hAP(?:\s*ax?\d*)?|CCR\d+|RB\d+[A-Z0-9]*)\b/i)
    if (mikrotik?.[1]) return productNormalizer.normalize(mikrotik[1]).replace(/\s+/g, '')

    const ubiquiti = raw.match(/\b(U6[A-Z\-]*|USW[A-Z0-9\-]*|UDM[A-Z0-9\-]*)\b/i)
    if (ubiquiti?.[1]) return productNormalizer.normalize(ubiquiti[1])

    const fury = raw.match(/\bFury\s*([A-Z0-9]+)?/i)
    if (fury && /kingston/i.test(raw)) return fury[1] ? `FURY ${fury[1]!.toUpperCase()}` : 'FURY'

    const vengeance = raw.match(/\bVengeance\s*([A-Z0-9]+)?/i)
    if (vengeance && /corsair/i.test(raw)) {
      return vengeance[1] ? `VENGEANCE ${vengeance[1]!.toUpperCase()}` : 'VENGEANCE'
    }

    if (partNumber) {
      // derive model from part number families
      if (/^SNV3/i.test(partNumber)) return 'NV3'
      if (/^WDS/i.test(partNumber) || /^SN770/i.test(partNumber)) return 'SN770'
    }

    // Fallback: first token after brand/line that looks like a model code
    const skip = new Set(
      [
        ...productNormalizer.tokenize(brand),
        ...productNormalizer.tokenize(line),
        ...productNormalizer.tokenize(category),
        'SSD',
        'RAM',
        'CPU',
        'GPU',
        'NOTEBOOK',
        'SERVIDOR',
        'PCIe',
        'PCIE',
        'NVME',
        'SATA',
      ].map((t) => t.toUpperCase()),
    )

    // Soft fallback: use strongest remaining token as model code
    for (const token of tokens) {
      if (skip.has(token)) continue
      if (/^\d+(GB|TB|MB)$/i.test(token)) continue
      if (/^(DDR|GEN|COM|DE|DA|DO|PARA|ROUTEROS)$/i.test(token)) continue
      if (/^[A-Z]{0,4}\d{2,}[A-Z0-9]*$/i.test(token) || /^[A-Z]+\d+[A-Z0-9]*$/i.test(token)) {
        return token
      }
    }

    if (line) return line
    if (brand) return productNormalizer.slug(brand).slice(0, 12)
    return 'UNKNOWN'
  }
}

function uniqueCi(values: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const value of values) {
    const key = value.toUpperCase()
    if (seen.has(key)) continue
    seen.add(key)
    result.push(value)
  }
  return result
}

function equalsCi(a: string, b: string): boolean {
  return productNormalizer.normalize(a) === productNormalizer.normalize(b)
}

function escapeReg(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function normalizeAttr(value: string): string {
  return productNormalizer.collapse(productNormalizer.stripAccents(value))
}

export const productParser = new ProductParser()
